import { useEffect, useRef, useState } from 'react';
import type { ApplicationApi, ApplicationManifest, ApplicationProps } from '@/applications/types';
import { DEFAULT_CAPABILITIES } from '@/applications/types';
import { asApplicationId, type SpatialObject } from '@/types';
import { OBJECT_KINDS, isObjectKind } from '@/core/scene/objectTypes';
import styles from './TerminalApp.module.css';

const MAX_LINES = 200;
/** Sentinel returned by `clear`; the component empties the scrollback instead of appending. */
export const CLEAR = '\u0000clear';

/**
 * A command line over the workspace. Commands operate on real data through the
 * application API; there is no shell emulation. Scrollback is application state, so
 * it survives minimize/restore and reloads.
 */
export const runCommand = (input: string, api: ApplicationApi): readonly string[] => {
  const [cmd = '', ...args] = input.trim().split(/\s+/);
  const arg = args.join(' ');
  const row = (o: SpatialObject) =>
    `${o.kind.padEnd(12)} ${(o.kind === 'file' ? o.fileName : o.name).padEnd(24)} ${o.id}`;
  switch (cmd.toLowerCase()) {
    case '':
      return [];
    case 'help':
      return ['help · ls [kind] · find <text> · select <id|name> · goto <id|name> · open <app> · apps · stats · clear'];
    case 'ls': {
      if (arg && !isObjectKind(arg)) return [`unknown kind "${arg}" — one of ${OBJECT_KINDS.join(', ')}`];
      const list = api.listObjects(isObjectKind(arg) ? arg : undefined);
      return list.length ? list.map(row) : ['(empty)'];
    }
    case 'find': {
      const q = arg.toLowerCase();
      const hits = api.listObjects().filter((o) => o.name.toLowerCase().includes(q));
      return hits.length ? hits.map(row) : [`no objects match "${arg}"`];
    }
    case 'select':
    case 'goto': {
      const target = api.listObjects().find((o) => o.id === arg || o.name.toLowerCase() === arg.toLowerCase());
      if (!target) return [`no object "${arg}"`];
      api.selectObjects([target.id]);
      if (cmd.toLowerCase() === 'goto') api.centerOn(target.id);
      return [`${cmd.toLowerCase()} ${target.name}`];
    }
    case 'apps':
      return api.applications.map((a) => `${a.icon} ${a.name.padEnd(12)} v${a.version}  ${a.category}`);
    case 'open': {
      const app = api.applications.find((a) => a.name.toLowerCase() === arg.toLowerCase() || a.id === arg);
      if (!app) return [`no application "${arg}"`];
      api.openApplication(app.id);
      return [`opened ${app.name}`];
    }
    case 'stats': {
      const doc = api.document;
      const objects = Object.values(doc.objects);
      const counts = new Map<string, number>();
      for (const o of objects) counts.set(o.kind, (counts.get(o.kind) ?? 0) + 1);
      return [
        `workspace ${doc.workspace.name}`,
        `objects   ${objects.length} (${[...counts].map(([k, n]) => `${k}:${n}`).join(' ') || 'none'})`,
        `links     ${Object.keys(doc.connections).length}`,
        `history   ${api.historyDepth} undoable steps`,
      ];
    }
    case 'clear':
      return [CLEAR];
    default:
      return [`unknown command "${cmd}" — try help`];
  }
};

export const TerminalApp = ({ state, setState, api, active }: ApplicationProps) => {
  const lines = typeof state.scrollback === 'string' && state.scrollback ? state.scrollback.split('\n') : [];
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (active && inputRef.current && document.activeElement === document.body) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [active]);
  useEffect(() => {
    outputRef.current?.scrollTo({ top: outputRef.current.scrollHeight });
  }, [lines.length]);

  const submit = () => {
    const out = runCommand(input, api);
    const next = out[0] === CLEAR ? [] : [...lines, `$ ${input}`, ...out].slice(-MAX_LINES);
    setState({ ...state, scrollback: next.join('\n') });
    setInput('');
  };

  return (
    <div
      className={styles.root}
      onClick={() => {
        inputRef.current?.focus();
      }}
    >
      <div ref={outputRef} className={styles.output} data-scrollable data-testid="terminal-output">
        {lines.map((l, i) => (
          <div key={i} className={l.startsWith('$ ') ? styles.cmd : undefined}>
            {l}
          </div>
        ))}
      </div>
      <div className={styles.prompt}>
        <span>$</span>
        <input
          ref={inputRef}
          className={styles.input}
          value={input}
          data-testid="terminal-input"
          aria-label="Command"
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => {
            setInput(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            e.stopPropagation();
          }}
        />
      </div>
    </div>
  );
};

export const terminalManifest: ApplicationManifest = {
  id: asApplicationId('app.terminal'),
  name: 'Terminal',
  version: '1.0.0',
  icon: '›_',
  category: 'developer',
  description: 'Command line over the workspace: list, find, select and open.',
  defaultSize: { width: 520, height: 320 },
  defaultPosition: 'cascade',
  capabilities: { ...DEFAULT_CAPABILITIES, observesWorkspace: true },
  component: TerminalApp,
  normalizeState: (state) => ({ scrollback: typeof state.scrollback === 'string' ? state.scrollback : '' }),
};
